import React, { useMemo } from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import SyncIcon from '@mui/icons-material/Sync';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { useLocation, useNavigate } from 'react-router-dom';
import {
	useAdminUserScreenersQuery,
	useAdminUserViewsQuery,
	useAdminUserWorksheetsQuery,
} from '../../api/admin';
import AdminQuickFilterToolbar from './AdminQuickFilterToolbar';

type ObjectType = 'View' | 'Worksheet' | 'Screener';

const TAB_FOR_TYPE: Record<ObjectType, string> = { View: 'views', Worksheet: 'worksheets', Screener: 'screeners' };
const CHIP_COLOR: Record<ObjectType, 'primary' | 'info' | 'warning'> = {
	View: 'primary',
	Worksheet: 'info',
	Screener: 'warning',
};

interface UserObjectRow {
	rowId: string;
	type: ObjectType;
	id: number;
	name: string;
	created_at: string | null;
	last_viewed: string | null;
	inputs: string[];
	universe: string[];
	shared_from_email: string | null;
	is_sync_share: boolean | null;
	admin_shared_by_email: string | null;
}

interface Props {
	userId: number;
	height?: number | string;
}

/** Views, worksheets and screeners in one grid, most recently created first. */
export default function UserObjectsTable({ userId, height = 380 }: Props) {
	const navigate = useNavigate();
	const location = useLocation();
	const views = useAdminUserViewsQuery(userId);
	const worksheets = useAdminUserWorksheetsQuery(userId);
	const screeners = useAdminUserScreenersQuery(userId);

	const rows = useMemo<UserObjectRow[]>(() => {
		const out: UserObjectRow[] = [];
		for (const v of views.data ?? []) {
			out.push({ rowId: `view-${v.id}`, type: 'View', id: v.id, name: v.name, created_at: v.created_at, last_viewed: v.last_viewed, inputs: v.inputs, universe: v.universe, shared_from_email: v.shared_from_email, is_sync_share: v.is_sync_share, admin_shared_by_email: v.admin_shared_by_email });
		}
		for (const w of worksheets.data ?? []) {
			out.push({ rowId: `worksheet-${w.id}`, type: 'Worksheet', id: w.id, name: w.name, created_at: w.created_at, last_viewed: w.last_viewed, inputs: w.inputs, universe: w.universe, shared_from_email: w.shared_from_email, is_sync_share: w.is_sync_share, admin_shared_by_email: w.admin_shared_by_email });
		}
		for (const s of screeners.data ?? []) {
			// screener has no created_at column on the platform; updated_at is the closest thing
			out.push({ rowId: `screener-${s.id}`, type: 'Screener', id: s.id, name: s.name, created_at: s.updated_at, last_viewed: null, inputs: s.inputs, universe: s.universe, shared_from_email: s.shared_from_email, is_sync_share: s.is_sync_share, admin_shared_by_email: s.admin_shared_by_email });
		}
		// newest first; objects without a created date go last
		out.sort((a, b) => (b.created_at ? Date.parse(b.created_at) : -Infinity) - (a.created_at ? Date.parse(a.created_at) : -Infinity));
		return out;
	}, [views.data, worksheets.data, screeners.data]);

	function openTab(type: ObjectType) {
		navigate({ search: `?tab=${TAB_FOR_TYPE[type]}` }, { state: location.state });
	}

	const columns: GridColDef[] = [
		{
			field: 'type',
			headerName: 'Type',
			width: 100,
			renderCell: (params: GridRenderCellParams) => (
				<Chip
					label={params.value}
					size="small"
					variant="outlined"
					color={CHIP_COLOR[params.value as ObjectType]}
					onClick={() => openTab(params.value as ObjectType)}
				/>
			),
		},
		{
			field: 'name',
			headerName: 'Name',
			flex: 1.5,
			minWidth: 180,
			renderCell: (params: GridRenderCellParams) => (
				<Link
					component="button"
					variant="body2"
					onClick={() => openTab(params.row.type as ObjectType)}
					sx={{ textAlign: 'left' }}
				>
					{params.value}
				</Link>
			),
		},
		{
			field: 'created_at',
			headerName: 'Created',
			width: 110,
			valueFormatter: ({ value }) => (value ? new Date(value).toLocaleDateString('en-CA') : ''),
		},
		{
			field: 'last_viewed',
			headerName: 'Last Viewed',
			width: 110,
			valueFormatter: ({ value }) => (value ? new Date(value).toLocaleDateString('en-CA') : ''),
		},
		{
			field: 'shared_from_email',
			headerName: 'Shared From',
			flex: 1,
			minWidth: 150,
			renderCell: (params: GridRenderCellParams) => {
				if (!params.value) return null;
				return (
					<Stack direction="row" spacing={0.5} alignItems="center">
						{params.row.is_sync_share && (
							<Tooltip title="Auto-synced"><SyncIcon fontSize="small" color="primary" /></Tooltip>
						)}
						{params.row.admin_shared_by_email && (
							<Tooltip title={`Shared by admin: ${params.row.admin_shared_by_email}`}>
								<AdminPanelSettingsIcon fontSize="small" color="warning" />
							</Tooltip>
						)}
						<Typography variant="body2">{params.value}</Typography>
					</Stack>
				);
			},
		},
	];

	return (
		<Box sx={{ height, width: '100%' }}>
			<DataGrid
				rows={rows}
				getRowId={(row) => row.rowId}
				columns={columns}
				loading={views.isLoading || worksheets.isLoading || screeners.isLoading}
				density="compact"
				disableSelectionOnClick
				components={{ Toolbar: AdminQuickFilterToolbar }}
				componentsProps={{ toolbar: { showQuickFilter: true } }}
			/>
		</Box>
	);
}
